import {
  InteractiveContainer,
  InteractiveContainerProps,
} from "@jugl-web/ui-kit";
import {
  assert,
  ClickAwayListener,
  cx,
  useTranslations,
} from "@jugl-web/utils";
import DOMPurify from "dompurify";
import linkifyHtml from "linkify-html";
import { FC, useEffect, useRef, useState } from "react";
import {
  RICH_TEXT_CONTENT_CLASS,
  RichTextarea,
  RichTextareaHandle,
} from "../RichTextarea";
import { TextToolbar } from "../TextToolbar";
import { ReactComponent as DescriptionIcon } from "./assets/description.svg";
import { EmojiButton } from "./EmojiButton";

interface DescriptionBoxProps {
  description: string;
  isEditable?: boolean;
  maxTextLength?: number;
  classes?: {
    descriptionText?: string;
    editingBox?: string;
  };
  onChange?: (description: string) => void;
}

export const DescriptionBox: FC<DescriptionBoxProps> = ({
  description,
  isEditable = true,
  classes,
  maxTextLength,
  onChange,
}) => {
  const [rawValue, setRawValue] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);

  const { t } = useTranslations();

  const richTextareaRef = useRef<RichTextareaHandle | null>(null);
  const richTextareaWrapperRef = useRef<HTMLDivElement | null>(null);

  const hasReachedMaxTextLength =
    typeof maxTextLength === "number" && rawValue.length >= maxTextLength;

  const handleRawTextChange = (text: string) => {
    setRawValue(text);
  };

  const handleClickAway = () => {
    assert(!!richTextareaRef.current, "RichTextarea ref is not defined");

    const previousDescription = description;

    const nextDescription = rawValue
      ? richTextareaRef.current.saveToHTML()
      : "";

    if (previousDescription !== nextDescription) {
      onChange?.(nextDescription);
    }

    setIsEditing(false);
  };

  const handleDescriptionClick: InteractiveContainerProps["onClick"] = (
    event
  ) => {
    const isLink = !!(event.target as HTMLElement).closest("a");

    if (!isLink) {
      setIsEditing(true);
    }
  };

  useEffect(() => {
    if (isEditing) {
      richTextareaRef.current?.readFromHTML(description);
      // Scroll to the rich textarea wrapper when editing starts
      // to ensure it's fully visible (along with the formatting toolbar)
      richTextareaWrapperRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [isEditing, description]);

  if (!isEditing) {
    return (
      <InteractiveContainer
        className={cx(
          "transition-color group min-h-[30px] rounded py-1 pr-4 duration-300",
          isEditable && "hover:bg-grey-100",
          classes?.descriptionText
        )}
        onClick={handleDescriptionClick}
        isDisabled={!isEditable}
        isTextSelectable={!isEditable}
      >
        {description ? (
          <div
            // oxlint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: linkifyHtml(
                DOMPurify.sanitize(description, { ADD_ATTR: ["target"] }),
                {
                  defaultProtocol: "https",
                  className: "text-primary-800",
                }
              ),
            }}
            className={cx(
              "overflow-hidden font-secondary text-[13px] leading-[140%] font-normal wrap-break-word text-dark-900 transition-transform duration-300",
              RICH_TEXT_CONTENT_CLASS,
              isEditable && "group-hover:translate-x-2"
            )}
          />
        ) : (
          <div
            className={cx(
              "flex items-center gap-1.5 transition-transform duration-300",
              isEditable && "group-hover:translate-x-2"
            )}
          >
            <DescriptionIcon />
            <span className="font-secondary text-sm leading-3 text-dark-500">
              {t({
                id: "description-box-component.add-description",
                defaultMessage: "Add description",
              })}
            </span>
          </div>
        )}
      </InteractiveContainer>
    );
  }

  return (
    <ClickAwayListener
      isDisabled={isEmojiPickerOpen}
      onClickAway={handleClickAway}
    >
      <div
        ref={richTextareaWrapperRef}
        className={cx(
          "flex flex-col overflow-hidden rounded-lg border border-solid border-[#DEDEE4] bg-white",
          classes?.editingBox
        )}
      >
        <RichTextarea
          ref={richTextareaRef}
          namespace="description-box"
          endSlot={
            <div className="flex flex-col gap-3 px-3 pb-3">
              {hasReachedMaxTextLength && (
                <span className="text-xs leading-2 text-tertiary-700">
                  {maxTextLength}/{maxTextLength}
                </span>
              )}
              <div className="flex items-center justify-between gap-2">
                <EmojiButton onOpenChange={setIsEmojiPickerOpen} />
                <TextToolbar />
              </div>
            </div>
          }
          placeholder={t({
            id: "description-box-component.enter-description",
            defaultMessage: "Enter description",
          })}
          classes={{
            placeholder:
              "text-grey-900 font-secondary leading-[140%] text-[13px] px-2 pt-2 pb-3",
            contentEditable:
              "jugl__custom-scrollbar max-h-[150px] overflow-y-auto outline-0 text-dark-900 font-secondary leading-[140%] font-normal text-[13px]",
            container: "px-2 pt-2 pb-3",
          }}
          onRawTextChange={handleRawTextChange}
          maxTextLength={maxTextLength}
        />
      </div>
    </ClickAwayListener>
  );
};
